import { delay, HttpResponse, http } from 'msw';
import { v4 as uuidv4 } from 'uuid';
import {
  CreateNoteBody,
  PatchLeadBody,
  PatchWorkflowBody,
} from '@/api/generated/endpoints/leads/leads.zod.ts';
import dealers from '@/mocks/dealers.json';
import { mockApiLeads } from '@/mocks/leads';
import { runtimeConfig } from '@/runtimeConfig.ts';
import { checkAuth } from './msal/auth_check';

const baseUrl = runtimeConfig.ApiBaseUrl.Or('/api/V1');

import type { LeadNoteCreator } from '@/models/entities/lead';

const mutableLeads = [...mockApiLeads()];
const notesStore: Record<
  string,
  Array<{
    id: string;
    content: string;
    createdAt: string;
    createdBy?: LeadNoteCreator;
  }>
> = {};

export const handlers = [
  http.get(`${baseUrl}/user`, async ({ request }) => {
    const authError = checkAuth(request);
    if (authError) return authError;

    await delay(150);
    return HttpResponse.json({
      dealerships: dealers,
    });
  }),
  http.get(`${baseUrl}/leads`, async ({ request }) => {
    const authError = checkAuth(request);
    if (authError) return authError;

    await delay(200);
    const url = new URL(request.url);
    const dealerIds = url.searchParams.getAll('dealerId');

    const filtered = mutableLeads.filter((l) => {
      return l.homeDealer?.id && dealerIds.includes(l.homeDealer.id);
    });

    return HttpResponse.json(filtered);
  }),

  http.patch(
    `${baseUrl}/leads/:leadId/workflow`,
    async ({ params, request }) => {
      const authError = checkAuth(request);
      if (authError) return authError;

      await delay(200);
      const { leadId } = params;

      const patch = PatchWorkflowBody.parse(await request.json());

      const leadIndex = mutableLeads.findIndex((l) => l.id === leadId);
      if (leadIndex === -1) {
        return new HttpResponse(null, { status: 404 });
      }

      if (patch.workFinishedAt && !mutableLeads[leadIndex].odometer) {
        return new HttpResponse(
          JSON.stringify({ error: 'Odometer reading is required' }),
          {
            status: 400,
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'client-app',
              ETag: 'odometer-missing',
            },
          },
        );
      }

      mutableLeads[leadIndex].workflow = {
        ...mutableLeads[leadIndex].workflow,
        ...patch,
      };

      return new HttpResponse(null, { status: 204 });
    },
  ),

  http.patch(`${baseUrl}/leads/:leadId`, async ({ params, request }) => {
    const authError = checkAuth(request);
    if (authError) return authError;

    await delay(200);
    const { leadId } = params;
    const json = await request.json();
    console.log(
      `[DEBUG_LOG] msw patchLead: leadId=${leadId}, json=${JSON.stringify(json)}`,
    );
    const patch = PatchLeadBody.parse(json);

    const leadIndex = mutableLeads.findIndex((l) => l.id === leadId);
    if (leadIndex === -1) {
      return new HttpResponse(null, { status: 404 });
    }

    mutableLeads[leadIndex] = {
      ...mutableLeads[leadIndex],
      ...patch,
      odometer: patch.odometer ?? undefined,
    };

    return new HttpResponse(null, { status: 204 });
  }),

  http.get(`${baseUrl}/leads/:leadId/notes`, async ({ params, request }) => {
    const authError = checkAuth(request);
    if (authError) return authError;

    await delay(100);
    const { leadId } = params as { leadId: string };

    if (!notesStore[leadId]) {
      notesStore[leadId] = [];
    }

    return HttpResponse.json(notesStore[leadId]);
  }),

  http.post(`${baseUrl}/leads/:leadId/notes`, async ({ params, request }) => {
    const authError = checkAuth(request);
    if (authError) return authError;

    await delay(150);
    const { leadId } = params as { leadId: string };
    const json = await request.json();
    const parsed = CreateNoteBody.parse(json);

    if (!notesStore[leadId]) {
      notesStore[leadId] = [];
    }

    const newNote = {
      id: uuidv4(),
      content: parsed.content,
      createdAt: new Date().toISOString(),
      createdBy: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@dealer.com',
      },
    };
    notesStore[leadId].push(newNote);

    return new HttpResponse(null, { status: 201 });
  }),
];
